import { useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Switch, TabsWrapper, TabsList, Tab } from '@livechat/design-system'
import useMedia from 'use-media'

import { ReactComponent as SettingsIcon } from '../assets/icons/settings.svg'
import { ReactComponent as ListIcon } from '../assets/icons/list-black.svg'
import { ReactComponent as WebhooksIcon } from '../assets/icons/webhooks.svg'

import SurveySettings from './SurveySettings'
import SurveyConnections from './SurveyConnections'

import SurveyQuestion from './SurveyQuestion'

const mainCss = css`
  display: flex;
  flex-direction: column;
  height: 100%;

  .name {
    width: 100%;
    padding-bottom: 5px;
    border-bottom: 2px solid #eaeaea;
    font-size: 24px;
  }

  .entries {
    border-radius: 4px;
    background-color: white;
    padding: 10px;
    box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

    display: flex;
    align-items: center;
  }

  .active {
    display: flex;
    align-items: center;
    border-radius: 4px;
    background-color: white;
    padding: 10px;
    box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

    > span {
      font-size: 16px;
      line-height: 22px;
    }
  }

  .top {
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;
  }

  .questions-label {
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 10px;
    width: 100%;
    padding-bottom: 5px;
    border-bottom: 2px solid #eaeaea;
  }

  .structure {
    margin-top: 10px;
    overflow: scroll;
    cursor: default;
    display: flex;
    flex-wrap: wrap;
  }

  .field {
    width: calc(100% - 30px);
    min-height: 65px;
    border-radius: 4px;
    background-color: white;
    padding: 15px;
    box-shadow: rgb(228 228 228) 0px 1px 5px 0px;
    margin-bottom: 10px;
  }

  .content {
    display: flex;
  }

  .answers {
    cursor: default;
    margin: 5px 0px 5px 18px;

    .lc-checkbox__square {
      transform: scale(0.85);
    }

    .lc-radio__circle {
      transform: scale(0.85);
    }
  }

  .answer {
    margin-top: 3px;
    display: flex;
    align-items: center;

    > span {
      margin-right: 5px;
    }

    > img {
      width: 17px;
      margin-top: 3px;
      margin-right: 5px;
    }
  }

  .label {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 10px;
    width: 100%;
    padding-bottom: 5px;
    border-bottom: 2px solid #eaeaea;
  }

  .drop {
    height: 36px;
    margin-top: auto;
    padding-left: calc(100% - 157px);

    .lc-btn {
      border-color: #d0d2d5;
      color: #424d57;
      font-weight: 400;
    }
  }

  .delete {
    margin-right: 5px;
  }

  .lc-badge {
    background-color: rgb(63, 140, 255);
    margin-right: 5px;
  }

  .question {
    display: flex;
  }

  .details {
    margin-top: 5px;
  }

  .badge {
    display: inline-block;
    padding-left: 5px;
    color: rgb(63, 140, 255);
    width: max-content;
    font-size: 15px;

    ::after {
      content: '•';
      font-weight: bold;
      margin-left: 5px;
    }

    :first-of-type {
      margin-left: 23px;
      padding-left: 0;
    }

    :last-of-type {
      ::after {
        content: '';
      }
    }
  }

  .lc-tab--selected {
    font-weight: 400;
  }

  .lc-tab {
    :hover {
      path {
        fill: #4384f5;
      }
    }
  }
`

const numberOfQuestionsCss = css`
  font-weight: 300;
  margin-left: 5px;
  display: inline-block;
`

const listIconCss = (isOpen) => css`
  margin-right: 5px;
  margin-bottom: -1px;
  fill: rgba(67, 78, 88, 0.9);

  ${isOpen &&
  css`
    fill: #4384f5;
  `};
`

const settingsIconCss = (isOpen) => css`
  margin-right: 5px;

  > path {
    fill: rgba(67, 78, 88, 0.9);
  }

  ${isOpen &&
  css`
    > path {
      fill: #4384f5;
    }
  `};
`

const SurveyDetails = ({
  survey,
  active,
  count = 0,
  structure,
  handleActive,
  isEditOpen,
}) => {
  const isSmall = useMedia({ maxWidth: 460 })
  const [tab, setTab] = useState('questions')

  const renderStructure = (struct) => {
    try {
      const formttedStructure =
        typeof structure === 'string' ? JSON.parse(struct) : struct

      return formttedStructure.map((item, index) => (
        <SurveyQuestion
          key={`${index}-survey-question-${item.question}`}
          item={item}
          index={index}
          isEditOpen={isEditOpen}
          surveyId={survey.id}
        />
      ))
    } catch (err) {
      console.log(err)
    }
  }

  const sentCount = survey?.sentCount || 0

  const sentCountCopy = isSmall
    ? `Sent ${sentCount} ${sentCount === 1 ? 'time' : 'times'}`
    : `Was sent ${sentCount} ${sentCount === 1 ? 'time' : 'times'}.`

  const answersCountCopy = isSmall
    ? `${count} answers`
    : `Has ${count} ${count === 1 ? 'answer' : 'answers'}.`

  const areQuestionsOpen = tab === 'questions'
  const areSettingsOpen = tab === 'settings'
  const areConnectionsOpen = tab === 'connections'

  return (
    <div css={mainCss}>
      <div className="top">
        <div
          css={css`
            display: flex;
          `}
        >
          <span
            className="entries"
            css={css`
              margin-right: 10px;
            `}
          >
            {sentCountCopy}
          </span>
          <span className="entries">{answersCountCopy}</span>
        </div>
        <div className="active">
          <span>Survey is {active ? 'active' : 'inactive'}</span>
          <Switch
            size="compact"
            css={css`
              margin-left: 5px;
              margin-top: 2px;
            `}
            onChange={() => {
              handleActive()
            }}
            on={active || false}
          />
        </div>
      </div>
      <TabsWrapper
        css={css`
          margin-bottom: 10px;
        `}
      >
        <TabsList>
          <Tab
            onClick={() => setTab('questions')}
            isSelected={areQuestionsOpen}
          >
            <ListIcon css={listIconCss(areQuestionsOpen)} />
            <span>Questions</span>
            <span css={numberOfQuestionsCss}>({structure.length})</span>
          </Tab>
          <Tab
            onClick={() => setTab('connections')}
            isSelected={areConnectionsOpen}
          >
            <WebhooksIcon css={settingsIconCss(areConnectionsOpen)} />
            <span>Connections</span>
          </Tab>
          <Tab onClick={() => setTab('settings')} isSelected={areSettingsOpen}>
            <SettingsIcon css={settingsIconCss(areSettingsOpen)} />
            <span>Setup</span>
          </Tab>
        </TabsList>
      </TabsWrapper>
      {areQuestionsOpen && (
        <div className="structure">{renderStructure(structure)}</div>
      )}
      {areConnectionsOpen && <SurveyConnections survey={survey} />}
      {areSettingsOpen && <SurveySettings survey={survey} />}
    </div>
  )
}

export default SurveyDetails
